import { useState, useEffect } from "react";
import FundRequestTable from "../../component/FundRequestTable";
import {
  getFundRequestBanks,
  getPaymentModeByBank,
  getFundOrderReport,
} from "../../../services/panel/panelServices";
import toast from "../../../utils/toast";
import Pagination from "../../component/Pagination";

// Fund request status: 0 All, 1 Requested, 2 Accepted, 3 Rejected
const STATUS_FILTERS = [
  { value: 0, label: "All" },
  { value: 1, label: "Requested" },
  { value: 2, label: "Accepted" },
  { value: 3, label: "Rejected" },
];

// No dates picked by default — the user chooses them
const defaultReportFilters = () => ({
  fromDate: "",
  toDate: "",
  tMode: 0, // payment mode id from BindPaymentMode, 0 = all
  status: 0,
  accountNo: "",
  transactionId: "",
});

const REPORT_TOP_ROWS = 50;
const PAGE_SIZE = 10;

export default function FundRequests() {
  const [isReportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [isFilterModeData, setFilterModeData] = useState([]);
  // Chrome ignores autoComplete="off" and fills the saved login mobile into
  // these text filters; it never fills a readOnly input, so unlock on focus
  const [filterTextUnlocked, setFilterTextUnlocked] = useState(false);
  const [reportFilters, setReportFilters] = useState(defaultReportFilters);
  // API has no paging (TopRows only), so page the returned rows here
  const [pageNumber, setPageNumber] = useState(1);
  const pageStart = (pageNumber - 1) * PAGE_SIZE;
  const pageRows = isReportData.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    fetchBankList();
    fetchFundOrderReport(defaultReportFilters());
  }, []);

  // IsRecent stays true until the user searches with changed filters
  const fetchFundOrderReport = async (filters) => {
    setReportLoading(true);
    try {
      const isRecent =
        JSON.stringify(filters) === JSON.stringify(defaultReportFilters());
      const payload = {
        FromDate: filters.fromDate,
        ToDate: filters.toDate,
        TMode: filters.tMode,
        Status: filters.status,
        AccountNo: filters.accountNo.trim(),
        TransactionID: filters.transactionId.trim(),
        TopRows: REPORT_TOP_ROWS,
        IsRecent: isRecent,
      };
      const res = await getFundOrderReport(payload);

      if (res?.statuscode === 1) {
        setReportData(res?.data || []);
      } else {
        setReportData([]);
      }
      setPageNumber(1);
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch fund order report",
        "error",
      );
    } finally {
      setReportLoading(false);
    }
  };

  const handleReportFilterChange = (e) => {
    const { name, value } = e.target;
    setReportFilters((f) => ({
      ...f,
      [name]: name === "status" || name === "tMode" ? Number(value) : value,
    }));
  };

  const handleReportSearch = () => {
    const { fromDate, toDate } = reportFilters;
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date can't be after To date.");
      return;
    }
    fetchFundOrderReport(reportFilters);
  };

  const handleReportReset = () => {
    const defaults = defaultReportFilters();
    setReportFilters(defaults);
    fetchFundOrderReport(defaults);
  };

  const fetchBankList = async () => {
    try {
      const res = await getFundRequestBanks();

      if (res?.statuscode === 1) {
        fetchFilterModes(res?.data || []);
      }
    } catch (err) {
      console.log(err.response?.data?.msg || "Failed to fetch banks", "error");
    }
  };

  // TMode filter = payment mode id; modes come per bank, so merge all banks' modes
  const fetchFilterModes = async (banks) => {
    try {
      const results = await Promise.all(
        banks.map((b) => getPaymentModeByBank(Number(b.id)).catch(() => null)),
      );
      const byId = new Map();
      results.forEach((res) => {
        if (res?.statuscode === 1) {
          (res?.data || []).forEach(
            (m) => !byId.has(m.id) && byId.set(m.id, m),
          );
        }
      });
      setFilterModeData([...byId.values()]);
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch payment modes",
        "error",
      );
    }
  };

  return (
    <>
      <div className="screen active" id="screen-fundrequests">
        <div className="page-head">
          <div>
            <h1>Fund requests</h1>
            <div className="ph-sub">
              Every fund request raised from this corporate account
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Fund requests</div>
         
          <div className="form-grid form-grid-4">
            <div className="field">
              <label>From date</label>
              <input
                type="date"
                name="fromDate"
                value={reportFilters.fromDate}
                max={reportFilters.toDate}
                onChange={handleReportFilterChange}
              />
            </div>
            <div className="field">
              <label>To date</label>
              <input
                type="date"
                name="toDate"
                value={reportFilters.toDate}
                min={reportFilters.fromDate}
                onChange={handleReportFilterChange}
              />
            </div>
            <div className="field">
              <label>Payment mode</label>
              <select
                name="tMode"
                value={reportFilters.tMode}
                onChange={handleReportFilterChange}
              >
                <option value={0}>All</option>
                {isFilterModeData.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.mode}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Status</label>
              <select
                name="status"
                value={reportFilters.status}
                onChange={handleReportFilterChange}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Account no</label>
              <input
                type="text"
                name="accountNo"
                autoComplete="off"
                readOnly={!filterTextUnlocked}
                onFocus={() => setFilterTextUnlocked(true)}
                value={reportFilters.accountNo}
                onChange={handleReportFilterChange}
                placeholder="Account number"
              />
            </div>
            <div className="field">
              <label>Transaction ID</label>
              <input
                type="text"
                name="transactionId"
                autoComplete="off"
                readOnly={!filterTextUnlocked}
                onFocus={() => setFilterTextUnlocked(true)}
                value={reportFilters.transactionId}
                onChange={handleReportFilterChange}
                placeholder="Transaction ID"
              />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleReportReset}
              disabled={reportLoading}
            >
              Reset
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleReportSearch}
              disabled={reportLoading}
            >
              {reportLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        <div className="card" style={{ marginTop: 20 }}>
          <FundRequestTable
            rows={pageRows}
            loading={reportLoading}
            startIndex={pageStart}
          />
          {!reportLoading && (
            <Pagination
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              totalRecords={isReportData.length}
              onChange={setPageNumber}
              label="requests"
            />
          )}
        </div>
      </div>
    </>
  );
}
